import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Menu,
  Link,
  Tabs,
  Tab,
  Tooltip,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import "./WorkspaceMembers.scss";
import CustomIcons from "../../assests/CustomIcons";
import MaterialIcons from "../../assests/MaterialIcons/MaterialIcons";
import { menuItemSX } from "../Builder/BuilderConst";
import { commonSelectStyles, commonMenuProps } from "../../theme/CommonStyles";
import {
  listWorkspaceMembersPaginatedAction,
  reinviteWorkspaceMemberAction,
  updateWorkspaceMemberAction,
  deleteWorkspaceMemberAction
} from "../../redux/actions/WorkspaceActions";
import DeleteConfirmationModal from "../Modals/DeleteConfirmationModal";
import MemberModal from "../Modals/MemberModal";
import { WorkspaceMember } from "../../redux/slices/WorkspaceSlice";
import { UserModules } from "../../redux/slices/AuthSlice2";

const typographyStyle = {
  fontFamily: 'var(--font-family-manrope)',
  fontWeight: 400,
  fontSize: '16px'
};

const WorkspaceMembers = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openMemberModal, setOpenMemberModal] = useState(false);
  const [memberMode, setMemberMode] = useState<"add" | "edit">("add");
  const [selectedMember, setSelectedMember] = useState<WorkspaceMember | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<WorkspaceMember | null>(null);
  const [value, setValue] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  // Row action menu state
  const [openRowMenuIndex, setOpenRowMenuIndex] = useState<number | null>(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { members, pagination, loading } = useSelector((state: RootState) => state.workspace);
  const totalMembers = pagination?.totalItems || 0;
  const { modules } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (modules?.team === true) {
      dispatch(listWorkspaceMembersPaginatedAction({
        page,
        limit: rowsPerPage,
        keyword: debouncedSearch,
        status: value === 0 ? "active" : "pending",
        sort_by: sortConfig.key || undefined,
        sort_order: sortConfig.direction || undefined
      }));
    }
  }, [dispatch, page, rowsPerPage, debouncedSearch, value, modules?.team, sortConfig]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setPage(1);
    setSortConfig({ key: '', direction: '' });
  };

  const handleRowMenuClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setRowMenuAnchorEl(event.currentTarget);
    setOpenRowMenuIndex(index);
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
    setOpenRowMenuIndex(null);
  };

  const paginatedData = members;

  const handlePageChange = (event: any, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const handleSort = (key: string) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
  };

  const getSortArrow = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <MaterialIcons.ArrowDropUp className="arrow-up-animation" style={{ width: '20px' }} /> : <MaterialIcons.ArrowDropUp className="arrow-down-animation" style={{ width: '20px' }} />;
  };

  const handleReinvite = (id: string) => {
    dispatch(reinviteWorkspaceMemberAction(id))
      .unwrap()
      .then(() => dispatch(listWorkspaceMembersPaginatedAction({
        page,
        limit: rowsPerPage,
        keyword: debouncedSearch,
        status: value === 0 ? "active" : "pending",
        sort_by: sortConfig.key || undefined,
        sort_order: sortConfig.direction || undefined
      })));
  };

  const handleUpdateRole = (id: string, currentModules: UserModules, newRole: string) => {
    dispatch(updateWorkspaceMemberAction({
      id,
      data: { role: newRole, modules: currentModules }
    }))
      .unwrap()
      .then(() => dispatch(listWorkspaceMembersPaginatedAction({
        page,
        limit: rowsPerPage,
        keyword: debouncedSearch,
        status: value === 0 ? "active" : "pending",
        sort_by: sortConfig.key || undefined,
        sort_order: sortConfig.direction || undefined
      })));
  };

  const handleDeleteMember = () => {
    if (!memberToDelete) return;
    dispatch(deleteWorkspaceMemberAction(memberToDelete._id))
      .unwrap()
      .then(() => {
        setOpenDeleteModal(false);
        dispatch(listWorkspaceMembersPaginatedAction({
          page,
          limit: rowsPerPage,
          keyword: debouncedSearch,
          status: value === 0 ? "active" : "pending",
          sort_by: sortConfig.key || undefined,
          sort_order: sortConfig.direction || undefined
        }));
      });
  };

  const handleOpenAddModal = () => {
    setMemberMode("add");
    setSelectedMember(null);
    setOpenMemberModal(true);
  };

  const handleOpenEditModal = (member: WorkspaceMember) => {
    setMemberMode("edit");
    setSelectedMember(member);
    setOpenMemberModal(true);
    handleRowMenuClose();
  };

  const handleCloseMemberModal = () => {
    setOpenMemberModal(false);
  };

  const getInitials = (member: WorkspaceMember) => {
    const first = member.userId?.first_name?.[0] || "";
    const last = member.userId?.last_name?.[0] || "";
    return (first + last).toUpperCase() || member.userId?.email?.[0]?.toUpperCase() || "?";
  };

  const getFullName = (member: WorkspaceMember) => {
    const first = member.userId?.first_name || "";
    const last = member.userId?.last_name || "";
    return (first + " " + last).trim() || "—";
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      px: { xs: 1.2, sm: 2.5, md: 5 },
      pt: 5
    }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: 'var(--font-family-rubik)',
            fontSize: '32px',
            fontWeight: 600,
            color: '#000000',
            letterSpacing: '0.00938em'
          }}
        >
          Workspace Members
        </Typography>
        <Typography color="textSecondary" sx={{ ...typographyStyle, mt: 0.5 }}>
          Manage members and their module access for this workspace.
        </Typography>
      </Box>

      <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons={false} aria-label="workspace settings tabs" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab
          label="Team members"
          sx={{
            fontFamily: 'var(--font-family-manrope)',
            fontSize: 15,
            fontWeight: 500,
            color: '#374151',
            transition: 'color 0.25s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            py: 0.6,
            textTransform: 'capitalize'
          }}
        />
        <Tab
          label="Pending invites"
          sx={{
            fontFamily: 'var(--font-family-manrope)',
            fontSize: 15,
            fontWeight: 500,
            color: '#374151',
            transition: 'color 0.25s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            py: 0.6,
            textTransform: 'capitalize'
          }}
        />
      </Tabs>

      <Box sx={{ mt: 3.5 }}>
        <Box sx={{ backgroundColor: 'var(--background-color)', maxWidth: '700px' }}>
          <div className="workspace-members-table-wrapper">
            {/* Controls row */}
            <div className="table-controls">
              <TextField
                variant="outlined"
                className="search-box"
                placeholder="Search member..."
                size="small"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                sx={{
                  backgroundColor: "var(--action-button-background-color)",
                  color: "white",
                  borderRadius: "var(--action-button-radius)",
                  border: "none",
                  padding: "var(--buttons-padding)",
                  fontSize: "var(--medium-body-font-size)",
                  cursor: "pointer",
                  textTransform: "none",
                  fontWeight: "400 !important",
                  fontFamily: "var(--font-family-manrope)",
                  width: "auto",
                  whiteSpace: "nowrap",
                }}
                startIcon={<CustomIcons.AddIcon style={{ width: 25 }} />}
                onClick={handleOpenAddModal}
              >
                Invite Member
              </Button>
            </div>

            {/* Table */}
            <div style={{ width: "100%", overflowX: "auto" }}>
              <table className="members-table">
                <thead>
                  <tr>
                    {value === 0 ? (
                      <th
                        style={{ width: "220px" }}
                        className={`sortable-header ${sortConfig.key === "name" ? "active" : ""}`}
                        onClick={() => handleSort("name")}
                      >
                        Name {getSortArrow("name")}
                      </th>
                    ) : (
                      <th
                        style={{ width: "220px" }}
                        className={`sortable-header ${sortConfig.key === "email" ? "active" : ""}`}
                        onClick={() => handleSort("email")}
                      >
                        Email {getSortArrow("email")}
                      </th>
                    )}
                    <th
                      style={{ width: "160px" }}
                      className={`sortable-header ${sortConfig.key === "role" ? "active" : ""}`}
                      onClick={() => handleSort("role")}
                    >
                      Role {getSortArrow("role")}
                    </th>
                    <th
                      style={{ width: "110px" }}
                      className={`sortable-header ${sortConfig.key === "createdAt" ? "active" : ""}`}
                      onClick={() => handleSort("createdAt")}
                    >
                      {value === 0 ? "Date added" : "Date invited"} {getSortArrow("createdAt")}
                    </th>
                    <th style={{ width: "40px", textAlign: "left" }}></th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    Array.from(new Array(3)).map((_, idx) => (
                      <tr key={idx}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Skeleton variant="circular" width={36} height={36} />
                            <div>
                              <Skeleton variant="text" width={120} height={20} />
                              <Skeleton variant="text" width={150} height={16} />
                            </div>
                          </div>
                        </td>
                        <td>
                          <Skeleton variant="rectangular" width={110} height={34} sx={{ borderRadius: "var(--small-border-radius)" }} />
                        </td>
                        <td>
                          <Skeleton variant="text" width={80} height={20} />
                        </td>
                        <td style={{ textAlign: "left" }}>
                          <Skeleton variant="circular" width={24} height={24} />
                        </td>
                      </tr>
                    ))
                  ) : paginatedData?.length > 0 ? (
                    paginatedData.map((row: WorkspaceMember, idx: number) => {
                      return (
                        <tr key={row._id}>
                          {/* Name / Email cell */}
                          {value === 0 ? (
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {/* Avatar */}
                                {row.profileImage ? (
                                  <img
                                    src={row.profileImage}
                                    alt="profile"
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    fontFamily: "var(--font-family-manrope)",
                                    flexShrink: 0,
                                  }}>
                                    {getInitials(row)}
                                  </div>
                                )}
                                <div>
                                  <div style={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    fontFamily: "var(--font-family-manrope)",
                                    color: "#111827",
                                    lineHeight: 1.3,
                                  }}>
                                    {getFullName(row)}
                                  </div>
                                  <div style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    fontFamily: "var(--font-family-manrope)",
                                  }}>
                                    {row.userId?.email || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>
                          ) : (
                            <td>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                {/* Avatar with email initial */}
                                {row.profileImage ? (
                                  <img
                                    src={row.profileImage}
                                    alt="profile"
                                    style={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontWeight: 600,
                                    fontSize: 13,
                                    fontFamily: "var(--font-family-manrope)",
                                    flexShrink: 0,
                                  }}>
                                    {(row.email || row.userId?.email || "?").charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div style={{
                                  fontSize: "14px",
                                  fontFamily: "var(--font-family-manrope)",
                                  color: "#374151",
                                  fontWeight: 500,
                                }}>
                                  {row.email || row.userId?.email || "—"}
                                </div>
                              </div>
                            </td>
                          )}

                          {/* Role select */}
                          <td>
                            <Select
                              size="small"
                              value={row?.owner === true ? "owner" : (row?.role || "user")}
                              onChange={(e) => handleUpdateRole(row._id, row.modules as unknown as UserModules, e.target.value)}
                              disabled={row?.owner === true}
                              sx={{
                                ...commonSelectStyles,
                                width: "auto",
                                minWidth: 110,
                                height: 34,
                              }}
                              MenuProps={commonMenuProps}
                            >
                              {row?.owner === true && (
                                <MenuItem value="owner" sx={menuItemSX}>Owner</MenuItem>
                              )}
                              <MenuItem value="user" sx={menuItemSX}>User</MenuItem>
                              <MenuItem value="admin" sx={menuItemSX}>Admin</MenuItem>
                            </Select>
                          </td>

                          {/* Date */}
                          <td>
                            <span style={{
                              fontSize: "14px",
                              fontFamily: "var(--font-family-manrope)",
                              color: "#374151",
                              whiteSpace: "nowrap",
                            }}>
                              {formatDate(row.createdAt || row.updatedAt)}
                            </span>
                          </td>

                          {/* Action menu */}
                          <td style={{ textAlign: "left" }}>
                            {row?.owner === true ? (
                              /* Owner — show tooltip, block menu open */
                              <Tooltip
                                title="Owner cannot be edited or removed"
                                placement="top"
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#ffffff",
                                      color: "#5e5d5dff",
                                      border: "1px solid #d8d8d8",
                                      fontSize: "12px",
                                      borderRadius: "var(--small-border-radius)",
                                      fontFamily: "var(--font-family-manrope)",
                                      fontWeight: 500,
                                      p: 1,
                                    },
                                  },
                                  arrow: {
                                    sx: { color: "#d8d8d8" },
                                  },
                                }}
                              >
                                <span style={{ display: "inline-flex" }}>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: "#d1d5db",
                                      cursor: "default",
                                      "&:hover": { backgroundColor: "transparent" },
                                    }}
                                    disableRipple
                                  >
                                    <MaterialIcons.MoreVerticalDots style={{ width: "20px" }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              /* Normal member — clickable menu */
                              <IconButton
                                size="small"
                                onClick={(e) => handleRowMenuClick(e, idx)}
                                sx={{ color: "#6b7280" }}
                              >
                                <MaterialIcons.MoreVerticalDots style={{ width: "20px" }} />
                              </IconButton>
                            )}

                            <Menu
                              id={`row-menu-${idx}`}
                              anchorEl={rowMenuAnchorEl}
                              open={openRowMenuIndex === idx}
                              onClose={handleRowMenuClose}
                              MenuListProps={{ 'aria-labelledby': 'row-action-button' }}
                              PaperProps={{
                                sx: {
                                  mt: 0.5,
                                  minWidth: 180,
                                  p: 0.5,
                                  boxShadow: 'none !important',
                                  borderRadius: 'var(--medium-border-radius) !important',
                                  border: '1px solid #d8d8d8 !important',
                                }
                              }}
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                              {/* Edit Permissions — only for active (non-pending) non-owner members */}
                              {value === 0 && (
                                <MenuItem
                                  onClick={() => handleOpenEditModal(row)}
                                  disabled={row?.owner === true || row?.status === "pending"}
                                  sx={{
                                    gap: "8px",
                                    py: 1.25,
                                    mx: 0.5,
                                    borderRadius: "var(--small-border-radius)",
                                    fontSize: "14px",
                                    fontFamily: "var(--font-family-manrope)",
                                    "&:hover": {
                                      backgroundColor: "#f9f9f9 !important",
                                      borderRadius: "var(--small-border-radius)",
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: "#e3f2fd !important",
                                      fontWeight: 600,
                                      borderRadius: "var(--small-border-radius)",
                                    },
                                    "&.Mui-focusVisible": {
                                      backgroundColor: "#e3f2fd !important",
                                    },
                                  }}
                                >
                                  <CustomIcons.PencilEdit style={{ width: "16px" }} />
                                  <Link sx={{ color: "#000000", textTransform: "none", textDecoration: "none" }}>
                                    Edit Permissions
                                  </Link>
                                </MenuItem>
                              )}

                              {/* Resend invite — only for pending tab */}
                              {value === 1 && (
                                <MenuItem
                                  onClick={() => { handleReinvite(row._id); handleRowMenuClose(); }}
                                  sx={{
                                    gap: "8px",
                                    py: 1.25,
                                    mx: 0.5,
                                    borderRadius: "var(--small-border-radius)",
                                    fontSize: "14px",
                                    fontFamily: "var(--font-family-manrope)",
                                    "&:hover": {
                                      backgroundColor: "#f9f9f9 !important",
                                      borderRadius: "var(--small-border-radius)",
                                    },
                                    "&.Mui-focusVisible": {
                                      backgroundColor: "#e3f2fd !important",
                                    },
                                  }}
                                >
                                  <CustomIcons.Refresh style={{ width: "16px" }} />
                                  <Link sx={{ color: "#000000", textTransform: "none", textDecoration: "none" }}>
                                    Resend invite
                                  </Link>
                                </MenuItem>
                              )}

                              {/* Remove / Cancel invite */}
                              <MenuItem
                                onClick={() => {
                                  handleRowMenuClose();
                                  setMemberToDelete(row);
                                  setOpenDeleteModal(true);
                                }}
                                disabled={row?.owner === true}
                                sx={{
                                  gap: "8px",
                                  py: 1.25,
                                  mx: 0.5,
                                  borderRadius: "var(--small-border-radius)",
                                  fontSize: "14px",
                                  fontFamily: "var(--font-family-manrope)",
                                  "& svg": {
                                    fill: "red",
                                    stroke: "red",
                                  },
                                  "& a": {
                                    color: "red",
                                  },
                                  "&:hover": {
                                    backgroundColor: "#fceced !important",
                                    borderRadius: "var(--small-border-radius)",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "#fceced !important",
                                    fontWeight: 600,
                                    borderRadius: "var(--small-border-radius)",
                                  },
                                  "&.Mui-focusVisible": {
                                    backgroundColor: "#fceced !important",
                                  },
                                }}
                              >
                                <CustomIcons.Delete style={{ width: "16px" }} />
                                <Link sx={{ color: "#000000", textTransform: "none", textDecoration: "none" }}>
                                  {value === 1 ? "Cancel Invite" : "Remove member"}
                                </Link>
                              </MenuItem>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        style={{ textAlign: "center", padding: "32px", color: "#9ca3af" }}
                      >
                        No members found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              {(loading || totalMembers > 0) && (
                <>
                  {loading ? (
                    <span>
                      <Skeleton variant="text" width={180} />
                    </span>
                  ) : (
                    <span>
                      {(page - 1) * rowsPerPage + 1}–
                      {Math.min(page * rowsPerPage, totalMembers)} of{" "}
                      {totalMembers} results shown
                    </span>
                  )}

                  <div className="page-controls">
                    <Pagination
                      count={Math.ceil(totalMembers / rowsPerPage)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />

                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      size="small"
                      sx={{
                        ml: 2,
                        "& .MuiInputBase-root": {
                          backgroundColor: "#e3f2fd !important",
                          borderRadius: "var(--small-border-radius)",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "var(--small-border-radius)",
                        },
                      }}
                    >
                      {[10, 20].map((val: number) => (
                        <MenuItem sx={menuItemSX} key={val} value={val}>
                          {val} / page
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>
        </Box>
      </Box>

      <MemberModal
        open={openMemberModal}
        handleClose={handleCloseMemberModal}
        mode={memberMode}
        member={selectedMember}
      />

      <DeleteConfirmationModal
        open={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDeleteMember}
        title={value === 1 ? "Cancel Invite" : "Remove Member"}
        msg={
          value === 1
            ? "Are you sure you want to cancel this invite? This action cannot be undone."
            : "Are you sure you want to remove this member? This action cannot be undone."
        }
      />
    </Box>
  );
};

export default WorkspaceMembers;
